import * as React from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { client } from 'core/graphql/client';

import { useReactiveVar } from '@apollo/client';
import { authState, LmsRoles } from 'core';
import {
  logout,
  useActiveMenu,
  useGoToErrorPage,
  useMyCareerCenter,
  useRefreshToken,
  useUser,
} from 'core/hooks';
import { PATHS } from 'router/config';
import {
  IMenuStructure,
  MenuItems,
  StudentMenu,
  TeacherMenu,
  MethodistMenu,
  UserRole,
} from 'context';
import { getToken } from 'services/AuthService';

import { HeaderComponent } from './Header';

const transformMenu = (
  menu: IMenuStructure[],
  menuCount: number,
  activeMenuItem: MenuItems | undefined,
) =>
  menu.map((menuItem) =>
    menuItem.hasCount
      ? { ...menuItem, isActive: menuItem.name === activeMenuItem, subMenuCount: menuCount }
      : { ...menuItem, isActive: menuItem.name === activeMenuItem },
  );

export const Header = memo(() => {
  const { t } = useTranslation();
  const { user: userData, loading, error } = useUser();
  const activeMenu = useActiveMenu();
  const goToErrorPage = useGoToErrorPage();
  const navigate = useNavigate();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const { updateTokens } = useRefreshToken();
  // Career Center is visible only to enrolled students; `myCareerCenter` being
  // non-null IS the membership check (spec 0018 §6). The hook self-skips for
  // non-students, so this costs nothing on a teacher/methodist session.
  const { isMember: isCareerMember, isPreview } = useMyCareerCenter();

  const demoMenu = useMemo(
    () => [
      {
        name: MenuItems.Courses,
        children: `${t('menu.student.courses')}`,
        to: PATHS.courses,
        path: PATHS.courses,
        hasCount: true,
        subMenuCount: userData.groupsCount || 0,
        isActive: MenuItems.Courses === activeMenu,
        disabled: false,
      },
    ],
    [activeMenu, t, userData.groupsCount],
  );

  const studentMenu = useMemo(
    () =>
      isCareerMember
        ? StudentMenu
        : StudentMenu.filter((menuItem) => menuItem.name !== MenuItems.CareerCenter),
    [isCareerMember],
  );

  // 🔴 У методиста карьерные пункты тоже условны. Зашитые намертво, они
  // отправляли бы его на 404 ИЗ СОБСТВЕННОГО меню, если сервер предпросмотр не
  // выдал (флаг выключен, старая версия бэкенда, направление не настроено).
  // У студента такая защита была с самого начала, у методиста — нет.
  const methodistMenu = useMemo(
    () =>
      isPreview ? MethodistMenu : MethodistMenu.filter((item) => item.name === MenuItems.Courses),
    [isPreview],
  );

  const finalMenu = useMemo(() => {
    switch (active_role) {
      case UserRole.STUDENT:
        return transformMenu(studentMenu, userData.groupsCount, activeMenu);
      case UserRole.TEACHER:
        return transformMenu(TeacherMenu, userData.groupsCount, activeMenu);
      default:
        return transformMenu(methodistMenu, userData.groupsCount, activeMenu);
    }
  }, [active_role, studentMenu, methodistMenu, userData.groupsCount, activeMenu]);

  useEffect(() => {
    if (error) {
      console.log({ headerError: error });
      goToErrorPage();
    }
  }, [error, goToErrorPage]);

  const onChangeRole = useCallback(
    async (role: UserRole) => {
      const refresh_token = getToken();
      if (!refresh_token) {
        return;
      }

      const isRoleApplied = await updateTokens(
        refresh_token,
        LmsRoles[role as keyof typeof LmsRoles],
      );
      if (!isRoleApplied) {
        return;
      }

      navigate(PATHS.courses, { replace: true });

      // resetStore, not clearStore: the role lives only in the JWT, so no query
      // variable changes and nothing else would refetch the groups/courses.
      try {
        await client.resetStore();
      } catch (e) {
        // resetStore rejects if any active query fails to refetch — the role
        // has already switched, so this is worth seeing but not worth blocking.
        console.error({ roleSwitchRefetchError: e });
      }
    },
    [updateTokens, navigate],
  );

  return (
    <HeaderComponent
      onExit={logout}
      name={isDemoUser ? t('header.demoUser') : userData.name}
      email={isDemoUser ? '' : userData.email}
      role={userData.role}
      hasRoles={userData.hasRoles}
      onChangeRole={onChangeRole}
      avatarImage={userData.avatarImage}
      menuItems={isDemoUser ? demoMenu : finalMenu}
      loading={loading}
    />
  );
});
